import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import type { EnvKey } from '@/app/site/scope';
import { CopyButton } from '@/shared/ui/CopyButton';
import { Segmented } from '@/shared/ui/Segmented';

import forms from '../FormsPage/FormsPage.module.scss';

type Sample = 'signup' | 'login' | 'own';

const CODE: Record<Sample, string> = {
  signup: [
    "import { facetaFromEnv } from '@pet-p-team/faceta-sdk';",
    '',
    'const faceta = facetaFromEnv(process.env);',
    '',
    '// Регистрация. С подтверждением почты уходит письмо со ссылкой',
    '// на /auth/confirm?token=… вашего сайта.',
    'const { confirmation_required, session } = await faceta',
    '  .with({ visitorIP })',
    '  .auth.register({ email, password, data: { name } });',
    '',
    '// Страница /auth/confirm: код из адреса → сразу сессия.',
    'const session = await faceta.auth.confirm(query.token);',
    '',
    '// Сброс пароля: письмо со ссылкой на /auth/reset?token=…',
    'await faceta.auth.forgotPassword(email);',
    'await faceta.auth.resetPassword(query.token, newPassword);',
  ].join('\n'),
  login: [
    '// Вход: 401 — неверная почта или пароль, 403 — почта не подтверждена.',
    'const session = await faceta.with({ visitorIP }).auth.login(email, password);',
    '',
    '// Токены — в httpOnly-cookie сайта, не в localStorage.',
    "setCookie(event, 'access', session.access_token, { httpOnly: true, maxAge: 3600 });",
    "setCookie(event, 'refresh', session.refresh_token, { httpOnly: true, maxAge: 2592000 });",
    '',
    '// Токен доступа живёт час — дальше новая пара (токен обновления одноразовый).',
    'const next = await faceta.auth.refresh(refreshToken);',
    '',
    '// Кто вошёл; null — не вошёл.',
    'const me = await faceta.with({ user: accessToken }).auth.me();',
    '',
    'await faceta.with({ user: accessToken }).auth.logout();',
  ].join('\n'),
  own: [
    '// Коллекция «Заказы»: читать — только своё, создавать — вошедшим,',
    '// менять и удалять — только своё («Модели» → «Настройки» → «Доступ с сайта»).',
    'const me = faceta.with({ user: accessToken });',
    '',
    "await me.create('orders', { items, address }); // заказ становится его",
    "const { items } = await me.list('orders'); // только его заказы",
    "await me.update('orders', id, { comment }); // чужой — 404",
    "await me.remove('orders', id);",
  ].join('\n'),
};

/** Как подключить вход на сайте: SDK, страницы для ссылок, правила «только своё». */
export function UsersApi({ siteId, env }: { siteId: string; env: EnvKey }) {
  const [sample, setSample] = useState<Sample>('signup');

  return (
    <div className={forms['api']}>
      <section className={forms['api__block']}>
        <h2 className={forms['api__title']}>Как это работает</h2>
        <ul className={forms['notes']}>
          <li>
            <b>Ключ сайта</b> — как у всего API (
            <Link
              to="/sites/$siteId/$env/settings"
              params={{ siteId, env }}
              search={{ tab: 'connect' }}
            >
              «Настройки → Подключение сайта»
            </Link>
            ); вошедший посетитель — токеном в заголовке <code>X-Faceta-User</code>, в SDK —{' '}
            <code>faceta.with({'{ user }'})</code>.
          </li>
          <li>
            <b>Страницы для ссылок из писем</b> — <code>/auth/confirm</code> и{' '}
            <code>/auth/reset</code> вашего сайта (меняются в «Настройках»): они берут{' '}
            <code>?token=</code> из адреса и передают в SDK.
          </li>
          <li>
            <b>Защита.</b> Вход и регистрация — с пределами частоты по адресу и по почте; неверная
            почта и неверный пароль отвечают одинаково; ловушка <code>trap</code> отсеивает ботов
            при регистрации.
          </li>
          <li>
            <b>Свои записи.</b> Правила «вошедшим» и «только своё» — в настройках любой коллекции:
            заказ создаёт покупатель и видит только свои.
          </li>
        </ul>
      </section>

      <section className={forms['api__block']}>
        <h2 className={forms['api__title']}>Код для сайта</h2>
        <Segmented
          value={sample}
          onValueChange={(next) => {
            setSample(next as Sample);
          }}
          aria-label="Пример кода"
          options={[
            { value: 'signup', label: 'Регистрация и пароль' },
            { value: 'login', label: 'Вход и сессия' },
            { value: 'own', label: 'Свои записи' },
          ]}
        />
        <div className={forms['code']}>
          <pre>{CODE[sample]}</pre>
          <CopyButton value={CODE[sample]} className={forms['code__copy'] ?? ''} />
        </div>
      </section>
    </div>
  );
}
